import wowsCommerce, { EVENTS, LOG_LEVELS } from '@wg/wows-commerce';
import { settings } from '~/utils/settings';
import store from '~/Store';
import { Settings } from '@wg/wows-commerce/settings';
import { changeVisiblePopup, hiddenMenuItem, setFetching, updateBundleList } from '~/Actions/ActionApp';
import {
    setBillingAddressSettings,
    setFastGoldProducts,
    setRegionalInfo,
    setTsv,
    setTsvManagementUrl,
    setWowsCommerce,
    setWsmartCurrentProduct,
    setWsmartPaymentMethod,
    shopUnavailable,
} from '~/Actions/ActionWSMart';
import { mergeBundles } from '~/utils/bundles';
import { ProductEntity } from '@wg/wows-commerce/core/entity/product.entity';
import { CATEGORIES } from '~/const';

export enum WoWsCommerceStorefrontNames {
    METASHOP = 'metashop',
    FASTGOLD = 'fastgold',
}

export default class WoWsCommerceHelper {
    private wowsCommerce: wowsCommerce = null;

    private defaultWowsCommerceSettings: Settings = {
        productsValidationEnabled: false,
        logLevel: window.metashop?.isStaff ? LOG_LEVELS.DEBUG : LOG_LEVELS.ERROR,
        backendUrl: settings.urls.wsmartUrl,
        language: settings.languageCode,
        showDevPanel: !!window.metashop?.isStaff,
        storefrontName: WoWsCommerceStorefrontNames.METASHOP,
        dwhExportUrl: settings.urls.dwhExport,
        userSupportUrl: settings.urls.supportUrl,
        userSetCountryUrl: settings.urls.setCountryUrl,
        fraudDetectionClosableByPlatform: true,
        fraudDetectionDialogEnabled: false,
        userAuthUrl: settings.urls.loginUrl,
        webPurchaseConfirmationIframeStyles: { 'background-color': 'transparent' },
        customPopupStyles: { 'background-color': 'transparent' },
        analytics: {
            projectName: WoWsCommerceStorefrontNames.METASHOP,
            sendInterval: 3,
            maxBatchSize: 5,
        },
        realm: settings.realm,
        appVersion: settings.appVersion,
        staticVersion: settings.staticVersion,
        isMaintenanceMode: settings.isMaintenanceMode,
        accountCompletionEnable: settings.accountCompletionEnabled,
    };

    private mergeProducts(products: ProductEntity[]) {
        if (!products?.length) {
            return;
        }
        const mergedBundles = mergeBundles(store.getState().ReducerApp.bundles, products);
        store.dispatch(updateBundleList(mergedBundles));
    }

    public async initialization() {
        if (!!this.defaultWowsCommerceSettings.backendUrl) {
            this.wowsCommerce = new wowsCommerce(this.defaultWowsCommerceSettings);

            this.wowsCommerce.subscribe(EVENTS.SHOP_UNAVAILABLE, ({ storefrontName }) => {
                if (storefrontName === WoWsCommerceStorefrontNames.METASHOP) {
                    store.dispatch(shopUnavailable());
                    store.dispatch(hiddenMenuItem(CATEGORIES.WSMART));
                } else {
                    store.dispatch(setFastGoldProducts([]));
                }
            });

            this.wowsCommerce.subscribe(EVENTS.OVERLAY_VISIBILITY, ({ visible }) => {
                store.dispatch(changeVisiblePopup(null, visible));
            });

            this.wowsCommerce.subscribe(EVENTS.ACCOUNT_UPDATE, ({ user }) => {
                if (user.billingAddress) {
                    store.dispatch(setBillingAddressSettings(user.billingAddress));
                }
                store.dispatch(setRegionalInfo(user.regional));
            });

            this.wowsCommerce.subscribe(EVENTS.CHANGE_CURRENT_PRODUCT, ({ currentProductId }) => {
                store.dispatch(setWsmartCurrentProduct(currentProductId));
            });

            this.wowsCommerce.subscribe(EVENTS.SET_FETCHING, ({ visibility }) => {
                store.dispatch(setFetching(visibility));
            });

            this.wowsCommerce.subscribe(EVENTS.PRODUCTS_UPDATE, ({ products, storefrontName }) => {
                if (storefrontName === WoWsCommerceStorefrontNames.METASHOP) {
                    this.mergeProducts(products);
                } else if (storefrontName === settings.fastGoldStorefrontName) {
                    store.dispatch(setFastGoldProducts(products));
                }
            });

            this.wowsCommerce.subscribe(EVENTS.PRODUCT_UPDATE, ({ product, storefrontName }) => {
                let products = store.getState().ReducerWSMart.products;

                if (storefrontName === settings.fastGoldStorefrontName) {
                    products = store.getState().ReducerWSMart.fastGoldProducts.map((fastFoldProduct) => fastFoldProduct.product);
                }

                products.forEach(function (part, index) {
                    if (this[index].id === product.id) {
                        this[index] = product;
                    }
                }, products);

                if (storefrontName === WoWsCommerceStorefrontNames.METASHOP) {
                    this.mergeProducts(products);
                } else if (storefrontName === settings.fastGoldStorefrontName) {
                    store.dispatch(setFastGoldProducts(products));
                }
            });

            this.wowsCommerce.subscribe(EVENTS.ACTIVE_PAYMENT_METHOD, ({ paymentMethod }) => {
                store.dispatch(setWsmartPaymentMethod(paymentMethod));
            });

            this.wowsCommerce.subscribe(EVENTS.TSV_MANAGEMENT_URL, ({ managementUrl }) => {
                store.dispatch(setTsvManagementUrl(managementUrl));
            });

            this.wowsCommerce.subscribe(EVENTS.SET_TSV, ({ tsv }) => {
                store.dispatch(setTsv(tsv));
            });

            store.dispatch(setWowsCommerce(this.wowsCommerce));

            await Promise.all([this.wowsCommerce.products(), this.wowsCommerce.categories(), this.wowsCommerce.user()]).then(([products, , user]) => {
                this.mergeProducts(products);
                if (user.billingAddress) {
                    store.dispatch(setBillingAddressSettings(user.billingAddress));
                }
                store.dispatch(setRegionalInfo(user.regional));
            });

            if (settings.fastGoldStorefrontName) {
                const products = await this.wowsCommerce.products(settings.fastGoldStorefrontName);
                store.dispatch(setFastGoldProducts(products));
            }
        }
    }
}
